import { format, eachDayOfInterval } from 'date-fns';
import {
  getResolutionStatus,
  getSessionPriority,
  isActionRequiredPriority
} from '../../../features/sentiment';

export const buildAgentDashboardStats = ({
  sessions = [],
  messages = [],
  totalLeads = 0,
  recentLeads = [],
  rangeStart,
  rangeEnd,
  locale = 'en'
}) => {
  const sessionIds = new Set(sessions.map((s) => s.id));
  const workspaceMessages = messages.filter((m) => sessionIds.has(m.session_id));

  const days = rangeStart && rangeEnd
    ? eachDayOfInterval({ start: rangeStart, end: rangeEnd })
    : [];

  const sessionsByDate = {};
  const messagesByDate = {};

  days.forEach((day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    sessionsByDate[dateStr] = 0;
    messagesByDate[dateStr] = 0;
  });

  sessions.forEach((session) => {
    if (!session.date_created) return;
    const date = new Date(session.date_created).toISOString().split('T')[0];
    if (sessionsByDate[date] !== undefined) {
      sessionsByDate[date]++;
    }
  });

  workspaceMessages.forEach((message) => {
    if (!message.date_created) return;
    const date = new Date(message.date_created).toISOString().split('T')[0];
    if (messagesByDate[date] !== undefined) {
      messagesByDate[date]++;
    }
  });

  const displayLocale = locale.startsWith('pl') ? 'pl-PL' : 'en-GB';
  const chartData = Object.keys(sessionsByDate)
    .sort()
    .map((date) => ({
      date,
      displayDate: new Date(date).toLocaleDateString(displayLocale, {
        day: 'numeric',
        month: 'short'
      }),
      sessions: sessionsByDate[date],
      messages: messagesByDate[date]
    }));

  const sessionsWithStatus = sessions.filter((session) => getResolutionStatus(session));
  const resolvedCount = sessionsWithStatus.filter((session) => {
    const status = getResolutionStatus(session);
    return status === 'resolved' || status === 'partially_resolved';
  }).length;

  const resolveRate = sessionsWithStatus.length > 0
    ? Math.round((resolvedCount / sessionsWithStatus.length) * 100)
    : null;

  const actionRequiredCount = sessions.filter((session) =>
    isActionRequiredPriority(getSessionPriority(session))
  ).length;

  const attentionSessions = sessions
    .filter((session) => isActionRequiredPriority(getSessionPriority(session)))
    .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
    .slice(0, 5)
    .map((session) => ({
      id: session.id,
      title: session.displayTitle || session.title || '-',
      priority: getSessionPriority(session),
      dateCreated: session.date_created
    }));

  return {
    totalSessions: sessions.length,
    totalMessages: workspaceMessages.length,
    totalLeads,
    resolveRate,
    resolvedCount,
    sessionsWithStatus: sessionsWithStatus.length,
    actionRequiredCount,
    attentionSessions,
    recentLeads,
    chartData,
    periodKey: 'currentWeek',
    dayCount: days.length
  };
};

export const attachFirstMessageTitles = (sessions, firstMessagesBySession = {}) =>
  sessions.map((session) => ({
    ...session,
    displayTitle: firstMessagesBySession[session.id] || session.title || null
  }));
